import React from 'react';
import { StaticImageData } from 'next/image';

import { TickCircle } from '@solent-university/solent-icons';

import Section from 'Elements/Section';
import Introduction from 'Elements/Introduction';
import ArrowLink, { ArrowLinkProps } from 'Elements/Link/Arrow';
import Box from 'Elements/Box';
import { H3 } from 'Elements/Typography';

import Grid, { Col } from 'Components/Grid';
import Video, { Props as VideoProps } from 'Components/Video';
import CarouselSupportBlock, { CarouselSupportBlockProps } from 'Components/SupportBlock/Carousel/';
import SupportBlock, { SupportBlockProps } from 'Components/SupportBlock';
import Accordion, { AccordionProps } from 'Components/Accordion';
import IconList, { IconListProps } from 'Components/List/IconList';
import InformationBlock from 'Components/InformationBlock';
import Card, { CardProps } from 'Components/Card';

import InformationBanner from 'Modules/Banner/Information';

import Course, { Props as CourseProps } from '../Course';

interface ContentProps {
    heading: string;
    text: React.ReactNode;
}

export interface Props extends CourseProps {
    introduction: string;
    overview: {
        text?: React.ReactNode;
        video?: VideoProps;
    }[];
    facts?: CarouselSupportBlockProps;
    sellingPoints?: AccordionProps[];
    whatWillLearn: ContentProps;
    leadsTo: ContentProps;
    learningAssessment: {
        heading: string;
        learnBy?: {
            heading: string;
            list?: IconListProps;
        };
        assessedBy?: {
            heading: string;
            list?: IconListProps;
        };
        text: React.ReactNode;
        image?: {
            src: StaticImageData | string;
        };
        link: ArrowLinkProps;
    };

    tuition: {
        subHeading: string;
        text: React.ReactNode;
    };
    extraCosts?: React.ReactNode;
    financialSupport: SupportBlockProps;
    howToApply: React.ReactNode;
    relatedCourses?: {
        cards: (CardProps & { id: string })[];
    };
}

const ProfessionalCourse: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        courseInformation,
        introduction,
        overview,
        facts,
        sellingPoints,
        whatWillLearn,
        learningAssessment,
        leadsTo,
        tuition,
        extraCosts,
        financialSupport,
        howToApply,
        courseApply,
        relatedCourses
    } = props;

    return (
        <Course
            breadcrumbs={breadcrumbs}
            heroBanner={heroBanner}
            courseInformation={courseInformation}
            courseApply={courseApply}
        >
            <Grid>
                <Col m={10} xl={7}>
                    <Section id="introduction">
                        <h2> About this course</h2>
                        <Introduction>{introduction}</Introduction>
                        <Box p={{ top: 2 }}>
                            {overview.map((item, index) => (
                                <React.Fragment key={index}>
                                    {item.text && item.text}
                                    {item.video && <Video {...item.video} />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Section>
                </Col>
                <Col m={10} xl={[9, 13]}>
                    {facts && (
                        <Section id="facts">
                            <CarouselSupportBlock {...facts} />
                        </Section>
                    )}
                    {sellingPoints && (
                        <Section id="why-course">
                            <SupportBlock heading="Why choose this course?">
                                {sellingPoints.map(item => (
                                    <Accordion {...item} key={item.id} />
                                ))}
                            </SupportBlock>
                        </Section>
                    )}
                </Col>
            </Grid>
            {/* === WHAT WILL LEARN / LEADS TO === */}
            <Section id="learning-leads-to">
                <Grid>
                    <Col m={10} l={6}>
                        <h2>{whatWillLearn.heading}</h2>
                        {whatWillLearn.text}
                    </Col>
                    <Col m={10} l={6}>
                        <h2>{leadsTo.heading}</h2>
                        {leadsTo.text}
                    </Col>
                </Grid>
            </Section>
            {/* ===== LEARNING ASSESSMENT ===== */}
            {learningAssessment.image && (
                <Section id="learning-assessment">
                    <Grid>
                        <Col m={10} l={12}>
                            <InformationBanner
                                heading={learningAssessment.heading}
                                image={learningAssessment.image}
                            >
                                {learningAssessment.learnBy && (
                                    <>
                                        <H3 size={{ base: '1.25rem', large: '1.5rem' }}>
                                            {learningAssessment.learnBy.heading}
                                        </H3>
                                        {learningAssessment.learnBy.list && (
                                            <IconList {...learningAssessment.learnBy.list} />
                                        )}
                                    </>
                                )}
                                {learningAssessment.assessedBy && (
                                    <>
                                        <H3 size={{ base: '1.25rem', large: '1.5rem' }}>
                                            {learningAssessment.assessedBy.heading}
                                        </H3>
                                        {learningAssessment.assessedBy.list && (
                                            <IconList {...learningAssessment.assessedBy.list} />
                                        )}
                                    </>
                                )}
                                {learningAssessment.text}
                                <ArrowLink {...learningAssessment.link} />
                            </InformationBanner>
                        </Col>
                    </Grid>
                </Section>
            )}
            <Grid>
                <Col>
                    <h2>Tuition fees</h2>
                </Col>
                <Col m={10} l={8}>
                    {/* === TUITION FEES === */}
                    <Section padding="none" id="tutition">
                        <p>{tuition.subHeading}</p>
                        {tuition.text}
                    </Section>
                    {/* === EXTRA costs === */}
                    {extraCosts && (
                        <Grid>
                            <Col l={11}>
                                <Section id="costs">
                                    <h2>Extra course costs</h2>
                                    {extraCosts}
                                </Section>
                            </Col>
                        </Grid>
                    )}
                </Col>
                {/* === FINANCIAL SUPPORT === */}
                <Col m={10} l={4}>
                    <SupportBlock {...financialSupport} />
                </Col>
                {/* === KEY ENTRY REQUIREMENTS */}
                <Col m={10} l={7}>
                    <Section id="entry">
                        <InformationBlock
                            headingType="h2"
                            heading="Key entry requirements"
                            icon={{ as: TickCircle }}
                        >
                            {howToApply}
                        </InformationBlock>
                    </Section>
                </Col>
            </Grid>
            {/* === RELATED COURSES === */}
            {relatedCourses && (
                <Section id="related-courses">
                    <Grid rowGap="1rem">
                        <Col>
                            <h2>Related courses</h2>
                        </Col>
                        {relatedCourses.cards.map(({ id, ...rest }) => (
                            <Col key={id} m={6} xl={4}>
                                <Card {...rest} />
                            </Col>
                        ))}
                    </Grid>
                </Section>
            )}
        </Course>
    );
};

export default ProfessionalCourse;
